/**
 * Vision Global Configuration
 * @package @newgenesis/vision
 */

export type VisionTheme = 'light' | 'dark' | 'glass';

export interface VisionGlobalConfig {
  theme: VisionTheme;
  autoDetect: boolean; // Auto-detect system theme
}

class VisionConfigManager {
  private config: VisionGlobalConfig = {
    theme: 'glass', // Default theme
    autoDetect: true
  };

  private listeners: Set<(config: VisionGlobalConfig) => void> = new Set();

  getConfig(): VisionGlobalConfig {
    return { ...this.config };
  }

  setTheme(theme: VisionTheme) {
    this.config.theme = theme;
    this.config.autoDetect = false; // Disable auto-detect when manually set
    this.notifyListeners();
  }

  setAutoDetect(enabled: boolean) {
    this.config.autoDetect = enabled;
    this.notifyListeners();
  }

  subscribe(callback: (config: VisionGlobalConfig) => void) {
    this.listeners.add(callback);
    return () => this.listeners.delete(callback);
  }

  private notifyListeners() {
    this.listeners.forEach(callback => callback(this.getConfig()));
  }

  // Get effective theme (considering auto-detect)
  getEffectiveTheme(): VisionTheme {
    if (!this.config.autoDetect) {
      return this.config.theme;
    }

    // Auto-detect system preference
    if (typeof window !== 'undefined') {
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      if (prefersDark && this.config.theme === 'glass') {
        return 'glass'; // Keep glass in dark mode
      }
    }

    return this.config.theme;
  }
}

export const visionConfig = new VisionConfigManager();

// Global Vision object for npm usage
if (typeof window !== 'undefined') {
  (window as any).Vision = {
    get theme() {
      return visionConfig.getConfig().theme;
    },
    set theme(value: VisionTheme) {
      visionConfig.setTheme(value);
    },
    get autoDetect() {
      return visionConfig.getConfig().autoDetect;
    },
    set autoDetect(value: boolean) {
      visionConfig.setAutoDetect(value);
    }
  };
}
